import { useEffect, useState } from "react";
import { OverlayTrigger, Popover, Tooltip } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import DstButton from "../../components/DstButton/DstButton";
import DstLiveTable from "../../components/DstTable/DstLiveTable";
import MultiFilterDropdown from "../../components/MultiFilterDropdown/MultiFilterDropdown";
import Popup from "../../components/Popup/Popup";
import { useSession } from "../../contexts/SessionContext";
import {
  ABSENCE_STATUS_VALUES,
  AbsenceStatus,
  getAbsenceBadgeClass,
  getAbsenceStatusColor,
  getAbsenceStatusIcon,
} from "../../services/MeetingAbsenceStatus";
import { PermissionComponent, validateUserPermission } from "../../services/PermissionGuard";
import { formatDateHumanly } from "../../services/TimeService";
import translate from "../../services/Translate";
import { completeNavigate } from "../../services/UtilityService";
import { AbsenceProof } from "./MeetingsAbsencesProofs.d";
import {
  fetchAbsenceProofDetails,
  fetchAuthorsAndAnimators,
  fetchFlags,
  generateRemoteUrl,
  updateAbsenceProofStatus,
} from "./MeetingsAbsencesProofs.function";

const MeetingsAbsencesProofs = () => {
  const { language, permissions } = useSession();
  const navigate = useNavigate();
  const [selectedItem, setSelectedItem] = useState<AbsenceProof | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [confirmLocked, setConfirmLocked] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [authors, setAuthors] = useState<{ id: number; full_name: string }[]>([]);
  const [animators, setAnimators] = useState<{ staff_member_id: number; full_name: string }[]>([]);
  const [loadingAuthorsAnimators, setLoadingAuthorsAnimators] = useState(false);
  const [flags, setFlags] = useState<{ id: number; name: string }[]>([]);
  const [loadingFlags, setLoadingFlags] = useState(false);
  const [filters, setFilters] = useState<{
    status: string[];
    authors: number[];
    animators: number[];
    flags: number[];
  }>({
    status: ["PENDING"],
    authors: [],
    animators: [],
    flags: [],
  });

  useEffect(() => {
    fetchAuthorsAndAnimators(setAuthors, setAnimators, setLoadingAuthorsAnimators, language);
    if (validateUserPermission([{ route: "/flags", methods: ["GET"] }], permissions)) {
      fetchFlags(language, setFlags, setLoadingFlags);
    }
  }, [language, permissions]);

  const columns = [
    {
      key: "dt_added",
      name: translate(language, "PAGES.MEETINGS_JUSTIFY_ABSENCE.COLUMNS.SUBMIT_DATE"),
      render: (item: AbsenceProof) => formatDateHumanly(language, item.dt_added, true),
      sortable: true,
    },
    {
      key: "user_full_name",
      name: translate(language, "PAGES.MEETINGS_JUSTIFY_ABSENCE.COLUMNS.LEARNER_NAME"),
      render: (item: AbsenceProof) => (
        <span
          className="text-primary clickable"
          onClick={(event) => {
            event.stopPropagation();
            completeNavigate(event, navigate, `/user/${item.user.id}`);
          }}
        >
          {item.user.full_name}
        </span>
      ),
    },
    {
      key: "meeting_name",
      name: translate(language, "PAGES.MEETINGS_JUSTIFY_ABSENCE.COLUMNS.MEETING_NAME"),
      render: (item: AbsenceProof) => item.meeting.name,
    },
    {
      key: "meeting_start",
      name: translate(language, "PAGES.MEETINGS_JUSTIFY_ABSENCE.COLUMNS.MEETING_DATE"),
      render: (item: AbsenceProof) => formatDateHumanly(language, item.meeting.dt_start, true),
    },
    {
      key: "meeting_animators",
      name: translate(language, "GLOBAL.ANIMATOR"),
      render: (item: AbsenceProof) => (
        <div>
          {item.meeting.animators?.length ? (
            item.meeting.animators.map((animator, index) => (
              <span key={animator.id}>
                <span
                  className="text-primary clickable"
                  onClick={(event) => {
                    event.stopPropagation();
                    completeNavigate(event, navigate, `/user/${animator.id}`);
                  }}
                >
                  {animator.full_name}
                </span>
                {index < item.meeting.animators!.length - 1 && ", "}
              </span>
            ))
          ) : (
            <span className="text-muted">N/A</span>
          )}
        </div>
      ),
    },
    {
      key: "status",
      name: translate(language, "GLOBAL.STATUS"),
      render: (item: AbsenceProof) => {
        const absenceStatus = item.status as AbsenceStatus;
        const validationTime = item.dt_validated;
        const content = (
          <span className={`d-flex align-items-center ${getAbsenceStatusColor(absenceStatus)} text-uppercase`}>
            {getAbsenceStatusIcon(absenceStatus)}
            {absenceStatus}
          </span>
        );
        return validationTime ? (
          <div className="d-flex align-items-center justify-content-between">
            {content}
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip className="xs-regular">{formatDateHumanly(language, validationTime, true)}</Tooltip>}
            >
              <span className="dst-icon-info-circle ms-3" />
            </OverlayTrigger>
          </div>
        ) : (
          content
        );
      },
      sortable: true,
    },
    {
      name: translate(language, "GLOBAL.FLAGS"),
      key: "user_flags",
      render: (item: AbsenceProof) => {
        const allFlags = item.user.flags || [];
        if (allFlags.length === 0) {
          return <span className="text-muted">N/A</span>;
        }

        const visibleFlags = allFlags.slice(0, 3);
        const remainingFlags = allFlags.slice(3);

        const popover = (
          <Popover id={`popover-flags-${item.user.id}-${item.meeting.id}`}>
            <Popover.Body>
              {remainingFlags.map((flag, index) => (
                <div key={`flag-${index}`} className="d-flex align-items-center mb-1">
                  <span className={`dst-icon-${flag.icon_name} me-2 blue-font`} />
                  <span>{flag.name}</span>
                </div>
              ))}
            </Popover.Body>
          </Popover>
        );

        return (
          <span className="d-flex align-items-center">
            {visibleFlags.map((flag, index) => (
              <OverlayTrigger
                key={`flag-visible-${index}`}
                trigger={["hover", "focus"]}
                placement="top"
                overlay={<Tooltip id={`tooltip-${flag.name}`}>{flag.name}</Tooltip>}
              >
                <span className={`dst-icon-${flag.icon_name} me-2 blue-font`} />
              </OverlayTrigger>
            ))}

            {remainingFlags.length > 0 && (
              <OverlayTrigger trigger={["hover", "focus"]} placement="top" overlay={popover}>
                <span className="rounded light-blue-background blue-font p-1 px-2 ms-1">+{remainingFlags.length}</span>
              </OverlayTrigger>
            )}
          </span>
        );
      },
    },
  ];

  return (
    <div className="m-3">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
        <h1 className="mb-0">{translate(language, "PAGES.MEETINGS_JUSTIFY_ABSENCE.TITLE")}</h1>
        <div className="d-flex gap-3 flex-wrap align-items-center">
          <PermissionComponent neededPermission={[{ methods: ["GET"], route: "/flags" }]}>
            <MultiFilterDropdown
              inputTitle={translate(language, "GLOBAL.FLAGS")}
              tags={flags.map((flag) => ({
                value: flag.id,
                display: flag.name,
              }))}
              onSelectTags={(tags) =>
                setFilters((prev) => ({
                  ...prev,
                  flags: tags.map(Number),
                }))
              }
              buttonWidth="120px"
              showSearchBar
              language={language}
              mock={loadingFlags}
              defaultValue={filters.flags}
            />
          </PermissionComponent>
          <MultiFilterDropdown
            inputTitle={translate(language, "GLOBAL.STATUS")}
            tags={ABSENCE_STATUS_VALUES.map((status) => status.toUpperCase())}
            defaultValue={filters.status}
            onSelectTags={(tags) =>
              setFilters((prev) => ({
                ...prev,
                status: tags.filter((tag): tag is string => !!tag),
              }))
            }
            language={language}
          />
          <MultiFilterDropdown
            inputTitle={translate(language, "GLOBAL.ORGANIZER")}
            tags={authors.map((author) => ({
              value: author.id,
              display: author.full_name,
            }))}
            onSelectTags={(tags) =>
              setFilters((prev) => ({
                ...prev,
                authors: tags.map(Number),
              }))
            }
            buttonWidth="120px"
            showSearchBar
            language={language}
            mock={loadingAuthorsAnimators}
            defaultValue={filters.authors}
          />
          <MultiFilterDropdown
            inputTitle={translate(language, "GLOBAL.ANIMATOR")}
            tags={animators.map((animator) => ({
              value: animator.staff_member_id,
              display: animator.full_name,
            }))}
            onSelectTags={(tags) =>
              setFilters((prev) => ({
                ...prev,
                animators: tags.map(Number),
              }))
            }
            buttonWidth="120px"
            showSearchBar
            language={language}
            mock={loadingAuthorsAnimators}
            defaultValue={filters.animators}
          />
          {(filters.status.length > 0 ||
            filters.authors.length > 0 ||
            filters.animators.length > 0 ||
            filters.flags.length > 0) && (
            <div
              className="ms-1 clickable dark-gray-font text-decoration-underline xs-regular"
              onClick={() =>
                setFilters({
                  status: [],
                  authors: [],
                  animators: [],
                  flags: [],
                })
              }
            >
              {translate(language, "PAGES.MEETINGS_JUSTIFY_ABSENCE.FILTERS.RESET_FILTERS")}
            </div>
          )}
        </div>
      </div>

      <DstLiveTable
        remote={generateRemoteUrl(filters.status, reloadKey, filters.authors, filters.animators, filters.flags)}
        remoteKey="meetings_absences_proofs"
        columns={columns}
        onRowClick={(_, item) => {
          setSelectedItem(item);
          setShowModal(true);
          fetchAbsenceProofDetails(item.meeting.id, item.user.id, language, (detailedProof: AbsenceProof) => {
            setSelectedItem((prev) => ({
              ...prev!,
              file_link: detailedProof.file_link,
            }));
          });
        }}
        pagination
        itemsPerPage={10}
        paginationResizeOptions={[10, 50, 100]}
        paginationAlwaysShowFooter
      />

      {selectedItem && (
        <Popup
          show={showModal}
          onClose={() => {
            setShowModal(false);
            setConfirmLocked(false);
          }}
          modalTitle={translate(language, "PAGES.MEETINGS_JUSTIFY_ABSENCE.MODAL.TITLE").replace(
            "{{NAME}}",
            selectedItem.user.full_name
          )}
          footerContent={
            selectedItem.status === "pending" ? (
              <div className="d-flex w-100 justify-content-end align-items-center gap-3">
                <div className="form-check m-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="confirm-lock"
                    checked={confirmLocked}
                    onChange={(event) => setConfirmLocked(event.target.checked)}
                  />
                  <label className="form-check-label s-regular ms-2" htmlFor="confirm-lock">
                    {translate(language, "PAGES.MEETINGS_JUSTIFY_ABSENCE.MODAL.BUTTONS.CONFIRM_LOCK")}
                  </label>
                </div>

                <div className="d-flex gap-2">
                  <DstButton
                    value={translate(language, "PAGES.MEETINGS_JUSTIFY_ABSENCE.MODAL.BUTTONS.APPROVE")}
                    variant="success"
                    disabled={!confirmLocked}
                    clickFunction={() =>
                      updateAbsenceProofStatus(
                        selectedItem.meeting.id,
                        selectedItem.user.id,
                        "validated",
                        language,
                        () => {
                          setShowModal(false);
                          setConfirmLocked(false);
                          setReloadKey((prev) => prev + 1);
                        }
                      )
                    }
                  />
                  <DstButton
                    value={translate(language, "PAGES.MEETINGS_JUSTIFY_ABSENCE.MODAL.BUTTONS.PRESENT")}
                    variant="warning"
                    disabled={!confirmLocked}
                    clickFunction={() =>
                      updateAbsenceProofStatus(
                        selectedItem.meeting.id,
                        selectedItem.user.id,
                        "present",
                        language,
                        () => {
                          setShowModal(false);
                          setConfirmLocked(false);
                          setReloadKey((prev) => prev + 1);
                        }
                      )
                    }
                  />
                  <DstButton
                    value={translate(language, "PAGES.MEETINGS_JUSTIFY_ABSENCE.MODAL.BUTTONS.REJECT")}
                    variant="danger"
                    disabled={!confirmLocked}
                    clickFunction={() =>
                      updateAbsenceProofStatus(
                        selectedItem.meeting.id,
                        selectedItem.user.id,
                        "declined",
                        language,
                        () => {
                          setShowModal(false);
                          setConfirmLocked(false);
                          setReloadKey((prev) => prev + 1);
                        }
                      )
                    }
                  />
                </div>
              </div>
            ) : (
              <div className="d-flex align-items-center gap-2">
                <span className={`badge ${getAbsenceBadgeClass(selectedItem.status as AbsenceStatus)} text-uppercase`}>
                  {selectedItem.status}
                </span>
                {selectedItem.validator && (
                  <span className="xs-regular">
                    {translate(language, "PAGES.MEETINGS_JUSTIFY_ABSENCE.MODAL.BY_VALIDATOR").replace(
                      "{{VALIDATOR}}",
                      selectedItem.validator
                    )}
                  </span>
                )}
              </div>
            )
          }
        >
          <div className="s-regular">
            <p>
              <strong>{translate(language, "PAGES.MEETINGS_JUSTIFY_ABSENCE.MODAL.SENT_ON")}</strong>{" "}
              {formatDateHumanly(language, selectedItem.dt_added, true)}
            </p>
            <p>
              <strong>{translate(language, "PAGES.MEETINGS_JUSTIFY_ABSENCE.MODAL.MEETING_NAME")}</strong>{" "}
              {selectedItem.meeting.name}
              <br />
              <strong>{translate(language, "PAGES.MEETINGS_JUSTIFY_ABSENCE.MODAL.MEETING_DATE")}</strong>{" "}
              {formatDateHumanly(language, selectedItem.meeting.dt_start, true)}
              <br />
              <strong>{translate(language, "PAGES.MEETINGS_JUSTIFY_ABSENCE.MODAL.ANIMATOR")}</strong>{" "}
              {selectedItem.meeting.animators?.length ? (
                selectedItem.meeting.animators.map((animator, index) => (
                  <span key={animator.id}>
                    <span
                      className="text-primary clickable"
                      onClick={(event) => {
                        event.stopPropagation();
                        completeNavigate(event, navigate, `/user/${animator.id}`);
                      }}
                    >
                      {animator.full_name}
                    </span>
                    {index < selectedItem.meeting.animators.length - 1 && ", "}
                  </span>
                ))
              ) : (
                <span className="text-muted">N/A</span>
              )}
            </p>
            <p className="mt-3">
              <strong>{translate(language, "PAGES.MEETINGS_JUSTIFY_ABSENCE.MODAL.JUSTIFICATION")}</strong>
              <br />
              {selectedItem.comment}
            </p>
            <div>
              <strong>{translate(language, "PAGES.MEETINGS_JUSTIFY_ABSENCE.MODAL.ATTACHMENT")}</strong>
              {selectedItem.filename ? (
                <div className="d-flex align-items-center mt-1 ">
                  <div className="me-2">{selectedItem.filename}</div>
                  {selectedItem.file_link ? (
                    <a
                      href={selectedItem.file_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-decoration-none d-flex align-items-center"
                    >
                      <span className="dst-icon-link-external-02 m-bold mb-0" />
                    </a>
                  ) : (
                    <div className="spinner-border spinner-border-sm text-secondary" role="status" />
                  )}
                </div>
              ) : (
                <span className="fst-italic text-muted ms-2">N/A</span>
              )}
            </div>
          </div>
        </Popup>
      )}
    </div>
  );
};

export default MeetingsAbsencesProofs;
